import React, { MouseEvent, ReactNode } from 'react';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';

import { useUserActions } from 'shared/auth/hooks/useUserActions';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import AccountMenu from './AccountMenu';

const LogoSvg = '/images/svg/logo_primary.svg';

const StyledAppBar = styled(AppBar)`
  && {
    background-color: ${COLORS.whiteBase};
    color: ${COLORS.blackBase};
    border-bottom: 1px solid ${COLORS.greyLight};
  }
`;

const StyledToolbar = styled(Toolbar)`
  && {
    display: flex;
    align-items: stretch;
    gap: ${SPACERS.small};
    height: var(--app-header-height);
    min-height: var(--app-header-height);
    padding: 0 ${SPACERS.medium};
    @media (max-width: 600px) {
      padding: 0 ${SPACERS.small};
      gap: ${SPACERS.tiny};
    }
  }
`;

const Brand = styled.div`
  display: flex;
  align-items: center;
  flex: 1 1 0;
  min-width: 0;
  gap: ${SPACERS.small};
`;

const Logo = styled.img`
  display: block;
  width: 152px;
  height: auto;
  @media (max-width: 600px) {
    width: 96px;
  }
`;

const BrandDivider = styled.span`
  display: block;
  width: 1px;
  height: 24px;
  background-color: ${COLORS.greyBase};
  @media (max-width: 600px) {
    display: none;
  }
`;

const OrgName = styled(Typography)`
  && {
    color: ${COLORS.greyDarkest};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    @media (max-width: 600px) {
      display: none;
    }
  }
`;

const NavSlot = styled.div`
  display: flex;
  align-items: stretch;
  flex: 0 1 auto;
  min-width: 0;
`;

const AccountSlot = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex: 1 1 0;
  min-width: 0;
`;

interface AppHeaderProps {
  nav?: ReactNode;
  hideAccountMenu?: boolean;
}

export default function AppHeader({ nav, hideAccountMenu }: AppHeaderProps) {
  const { organizationName } = useUserActions();
  const { push } = useHistory();
  const goHome = (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    push('/');
  };

  return (
    <StyledAppBar position="static" elevation={0} className="app-header">
      <StyledToolbar>
        <Brand>
          <a href="/" aria-label="True Impact home" onClick={goHome}>
            <Logo className="main-logo" alt="True Impact" src={LogoSvg} />
          </a>
          <BrandDivider aria-hidden="true" />
          <OrgName variant="body2" title={organizationName}>
            {organizationName}
          </OrgName>
        </Brand>
        <NavSlot>{nav}</NavSlot>
        <AccountSlot className="top-menu">
          {!hideAccountMenu && <AccountMenu />}
        </AccountSlot>
      </StyledToolbar>
    </StyledAppBar>
  );
}
